import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { FmParticipant, FmRoom } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService, IConfirmDialogConfig, SnConfirmDialogComponent } from '@lib/ui/dialog';

export const PARTICIPANT_TOKEN_KEY = 'participantToken';

@Injectable({ providedIn: 'root' })
export class FmRoomsAPI {
  public inviteUserConfirm;
  public joinLink;

  private _headers = new HttpHeaders();
  private _LiveVideoRecording$ = new Subject<any>();

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getLiveVideoRecording$(): Observable<any> {
    return this._LiveVideoRecording$.asObservable();
  }

  public getRoomList(params?: ISnListQueryParameters): Observable<ISnListResponse<FmRoom>> {
    return this._httpClient.get('/api/communications/video/rooms/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((room) => new FmRoom(room)),
      })),
      tap({
        error: () =>
          this._toastrService.error(t('vault.liveLinks.errors.couldNotRetrieve')),
      }),
    );
  }

  public createVideoRoom(data: any, promptSms?: boolean): Observable<FmRoom> {
    if (promptSms) {
      return this._dialogService
        .confirm({
          message: t('vault.liveLinks.confirmations.sendSmsInvite'),
          confirmButton: {
            text: t('common.send'),
            color: 'accent',
          },
        })
        .pipe(
          switchMap(() => {
            return this._httpClient.post('/api/communications/video/rooms/', data);
          }),
          map((room) => new FmRoom(room)),
          tap({
            next: () => this._toastrService.success(t('vault.liveLinks.success.videoRoomCreated')),
            error: () =>
              this._toastrService.error(t('vault.liveLinks.errors.unableToCreate')),
          }),
        );
    }
    return this._httpClient.post('/api/communications/video/rooms/', data).pipe(
      map((room) => new FmRoom(room)),
      tap({
        next: () => this._toastrService.success(t('vault.liveLinks.success.liveLinkCreated')),
        error: () =>
          this._toastrService.error(t('vault.liveLinks.errors.unableToCreate')),
      }),
    );
  }

  public getRoom(id: string): Observable<FmRoom> {
    return this._httpClient
      .get(`/api/communications/video/rooms/${id}/`)
      .pipe(map((room) => new FmRoom(room)));
  }

  public updateRoom(id: string, data: any): Observable<FmRoom> {
    return this._httpClient.patch(`/api/communications/video/rooms/${id}/`, data).pipe(
      map((room) => new FmRoom(room)),
      tap({
        next: () => this._toastrService.success(t('vault.liveLinks.success.liveLinkUpdated')),
        error: () =>
          this._toastrService.error(t('vault.liveLinks.errors.unableToUpdate')),
      }),
    );
  }

  public roomComplete(id: string): Observable<FmRoom> {
    return this._dialogService
      .confirm({
        message: t('vault.liveLinks.confirmations.completeLiveLink'),
        confirmButton: {
          text: t('vault.liveLinks.completeLiveLink'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() =>
          this._httpClient.post(`/api/communications/video/rooms/${id}/complete/`, null),
        ),
        map((room) => new FmRoom(room)),
        tap({
          next: () => this._toastrService.success(t('vault.liveLinks.success.liveLinkCompleted')),
          error: () =>
            this._toastrService.error(t('vault.liveLinks.errors.unableToComplete')),
        }),
      );
  }

  public joinRoom(id: string, data?: any): Observable<FmParticipant> {
    return this._httpClient.post(`/api/communications/video/rooms/${id}/join/`, data).pipe(
      map((room) => new FmParticipant(room)),
      tap({
        error: () =>
          this._toastrService.error(t('vault.liveLinks.errors.unableToJoin')),
      }),
    );
  }

  public joinPublicRoom(data: any): Observable<FmParticipant> {
    this._headers = new HttpHeaders({
      Authorization: `VideoRoomParticipantToken ${data.participantToken}`,
    });
    return this._httpClient
      .post(
        '/api/communications/video/public/rooms/join/',
        {},
        {
          headers: this._headers,
        },
      )
      .pipe(
        map((response) => new FmParticipant(response)),
        tap({
          next: () => localStorage.setItem(PARTICIPANT_TOKEN_KEY, data.participantToken),
          error: (err: HttpErrorResponse) => {
            const errMsg = err?.error?.error;
            if (errMsg) {
              this._toastrService.error(errMsg);
            } else {
              this._toastrService.error(t('vault.liveLinks.errors.unableToJoin'));
            }
          },
        }),
      );
  }

  public getParticipants(
    roomPk: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FmParticipant>> {
    return this._httpClient
      .get(`/api/communications/video/rooms/${roomPk}/participants/`, {
        params,
        headers: this._headers,
      })
      .pipe(
        map((response: any) => ({
          count: response.count,
          next: response.next,
          previous: response.previous,
          results: response.results.map((participant) => new FmParticipant(participant)),
        })),
      );
  }

  public confirm(config: IConfirmDialogConfig): Observable<boolean> {
    return this._matDialog
      .open(SnConfirmDialogComponent, { data: config, id: 'confirmDialog' })
      .afterClosed()
      .pipe(filter((confirm: boolean) => !!confirm));
  }

  public getPublicParticipants(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FmParticipant>> {
    return this._httpClient
      .get('/api/communications/video/public/participants/', {
        params,
        headers: this._headers,
      })
      .pipe(
        map((response: any) => ({
          count: response.count,
          next: response.next,
          previous: response.previous,
          results: response.results,
        })),
      );
  }

  public updateParticipant(
    roomId: string,
    participantId: string,
    data: any,
  ): Observable<FmParticipant> {
    return this._httpClient
      .patch(`/api/communications/video/rooms/${roomId}/participants/${participantId}/`, data)
      .pipe(map((room) => new FmParticipant(room)));
  }

  public removeParticpantByID(id: string, roomPk: string, data: any): Observable<FmParticipant> {
    return this._httpClient
      .post(`/api/communications/video/rooms/${roomPk}/participants/${id}/remove/`, data)
      .pipe(map((room) => new FmParticipant(room)));
  }

  public inviteUsers(id: string, data: any): Observable<FmParticipant> {
    return this._httpClient.post(`/api/communications/video/rooms/${id}/invite/`, data).pipe(
      map((room) => new FmParticipant(room)),
      tap({
        next: () => {
          this._toastrService.success(t('vault.liveLinks.success.inviteSent'));
        },
        error: () => {
          this._toastrService.error(t('vault.liveLinks.errors.unableToSendInvite'));
        },
      }),
    );
  }

  public getParticipant(roomId: string, participantId: string): Observable<FmParticipant> {
    return this._httpClient
      .get(`/api/communications/video/rooms/${roomId}/participants/${participantId}/`)
      .pipe(map((response: any) => new FmParticipant(response)));
  }

  public getParticipantByCode(pcode: string): Observable<any> {
    return this._httpClient.get(`/api/communications/video/public/participants/${pcode}/`);
  }

  public removeParticpant(roomId: string, participantId: string): Observable<any> {
    return this._dialogService
      .confirm({
        message: t('vault.liveLinks.confirmations.removeParticipant'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() =>
          this._httpClient.post(
            `/api/communications/video/rooms/${roomId}/participants/${participantId}/remove/`,
            null,
          ),
        ),
        tap({
          next: () => this._toastrService.success(t('vault.liveLinks.success.userRemoved')),
          error: () => this._toastrService.error(t('vault.liveLinks.errors.couldNotRemoveUser')),
        }),
      );
  }

  public deleteLiveRecoding(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('vault.liveLinks.confirmations.deleteLiveRecording'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/communications/video/recordings/${id}/`)),
        tap({
          next: () => {
            this._toastrService.success(t('vault.liveLinks.success.liveRecordingDeleted'));
            this._LiveVideoRecording$.next();
          },
          error: () =>
            this._toastrService.error(t('vault.liveLinks.errors.cannotDeleteRecording')),
        }),
      );
  }

  public deleteVideoRoom(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('vault.liveLinks.confirmations.deleteLiveLinkRoom'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/communications/video/rooms/${id}/`)),
        tap({
          next: () => {
            this._toastrService.success(t('vault.liveLinks.success.liveLinkRoomDeleted'));
          },
          error: () =>
            this._toastrService.error(t('vault.liveLinks.errors.cannotDeleteRoom')),
        }),
      );
  }
}
